import React from "react";
import { X, Calendar, User, ExternalLink } from "lucide-react";
import badgeStyles from "../utils/badgeStyles";
import formatDate from "../utils/formatDate";
import limitWords from "../utils/limitWords";
import ImageFound from "../images/Image_not_available.png";
import "./NewsCrawler.css"; // Import the shared CSS file

const ArticleModal = ({ selectedArticle, setSelectedArticle }) => {
  // Helper functions for badge classes using our updated badgeStyles helper
  const getTopicBadgeClass = (topic) => {
    if (!topic)
      return "article-modal__badge article-modal__badge--topic-default";
    return (
      badgeStyles.articleModal.topic[topic] ||
      "article-modal__badge article-modal__badge--topic-default"
    );
  };

  const getSentimentBadgeClass = (sentiment) => {
    if (!sentiment)
      return "article-modal__badge article-modal__badge--sentiment-default";
    return (
      badgeStyles.articleModal.sentiment[sentiment] ||
      "article-modal__badge article-modal__badge--sentiment-default"
    );
  };

  return (
    <div
      className="article-modal__overlay"
      onClick={(e) => e.target === e.currentTarget && setSelectedArticle(null)}
    >
      <div
        className="article-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="article-modal__image-container">
          <img
            src={selectedArticle.imageLink || ImageFound}
            alt={selectedArticle.title}
            className="article-modal__image"
            onLoad={(e) =>
              (e.target.style.transition = "transform 0.7s ease-in-out")
            }
          />
          <button
            onClick={() => setSelectedArticle(null)}
            className="article-modal__close-button"
          >
            <X size={20} />
          </button>
          <div className="article-modal__title-container">
            <h1 className="article-modal__title">{selectedArticle.title}</h1>
          </div>
        </div>

        <div className="article-modal__content">
          <div className="article-modal__badges">
            {selectedArticle.topic && (
              <span className={getTopicBadgeClass(selectedArticle.topic)}>
                {selectedArticle.topic}
              </span>
            )}
            {selectedArticle.sentiment && (
              <span
                className={getSentimentBadgeClass(selectedArticle.sentiment)}
              >
                {selectedArticle.sentiment}
              </span>
            )}
            {selectedArticle.bias === false && (
              <span className="article-modal__badge article-modal__badge--unbiased">
                Unbiased
              </span>
            )}
          </div>

          <div className="article-modal__meta">
            <div className="article-modal__meta-left">
              <div className="article-modal__meta-item article-modal__meta-author">
                <User size={16} className="article-modal__meta-icon" />
                <span>{selectedArticle.author || "Unknown"}</span>
              </div>
              <div className="article-modal__meta-item">
                <Calendar size={16} className="article-modal__meta-icon" />
                <span>{formatDate(selectedArticle.datePublished)}</span>
              </div>
            </div>
            <span className="article-modal__meta-source">
              {selectedArticle.newsChannel || "Unknown Source"}
            </span>
          </div>

          <div className="article-modal__summary-container">
            <h2 className="article-modal__summary-title">Summary</h2>
            <p className="article-modal__summary-text">
              {selectedArticle.summary || "No summary available"}
            </p>
          </div>

          <div>
            <h2 className="article-modal__content-title">Content</h2>
            <p className="article-modal__content-text">
              {limitWords(selectedArticle.content) || "No content available"}
            </p>
          </div>

          {(selectedArticle.bias !== undefined ||
            selectedArticle.intent ||
            selectedArticle.possibly_misinformed !== undefined ||
            selectedArticle.sensational !== undefined) && (
            <div className="article-modal__metrics">
              <h2 className="article-modal__metrics-title">
                Transparency Metrics
              </h2>
              <div className="article-modal__metrics-grid">
                {selectedArticle.intent && (
                  <div className="article-modal__metric">
                    <div className="article-modal__metric-indicator article-modal__metric-indicator--blue"></div>
                    <span className="article-modal__metric-label">Intent:</span>
                    &nbsp;{selectedArticle.intent}
                  </div>
                )}
                {selectedArticle.bias !== undefined && (
                  <div className="article-modal__metric">
                    <div className="article-modal__metric-indicator article-modal__metric-indicator--green"></div>
                    <span className="article-modal__metric-label">Bias:</span>
                    &nbsp;{selectedArticle.bias ? "Present" : "Not detected"}
                  </div>
                )}
                {selectedArticle.possibly_misinformed !== undefined && (
                  <div className="article-modal__metric">
                    <div className="article-modal__metric-indicator article-modal__metric-indicator--purple"></div>
                    <span className="article-modal__metric-label">
                      Misinformation:
                    </span>
                    &nbsp;
                    {selectedArticle.possibly_misinformed
                      ? "Possible"
                      : "Not detected"}
                  </div>
                )}
                {selectedArticle.sensational !== undefined && (
                  <div className="article-modal__metric">
                    <div className="article-modal__metric-indicator article-modal__metric-indicator--yellow"></div>
                    <span className="article-modal__metric-label">
                      Sensationalism:
                    </span>
                    &nbsp;
                    {selectedArticle.sensational ? "Present" : "Not detected"}
                  </div>
                )}
              </div>

              {selectedArticle.bias_reason && (
                <div className="article-modal__analysis">
                  <span className="article-modal__analysis-label">
                    Bias analysis:
                  </span>
                  &nbsp;
                  <span className="article-modal__analysis-text">
                    {selectedArticle.bias_reason}
                  </span>
                </div>
              )}

              {selectedArticle.misinformation_reason && (
                <div className="article-modal__analysis">
                  <span className="article-modal__analysis-label">
                    Misinformation analysis:
                  </span>
                  &nbsp;
                  <span className="article-modal__analysis-text">
                    {selectedArticle.misinformation_reason}
                  </span>
                </div>
              )}

              {selectedArticle.sensational_reason && (
                <div className="article-modal__analysis">
                  <span className="article-modal__analysis-label">
                    Sensationalism analysis:
                  </span>
                  &nbsp;
                  <span className="article-modal__analysis-text">
                    {selectedArticle.sensational_reason}
                  </span>
                </div>
              )}

              {selectedArticle.tone_explanation && (
                <div className="article-modal__analysis">
                  <span className="article-modal__analysis-label">
                    Tone analysis:
                  </span>
                  &nbsp;
                  <span className="article-modal__analysis-text">
                    {selectedArticle.tone_explanation}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="article-modal__actions">
            {selectedArticle.link && (
              <a
                href={selectedArticle.link}
                target="_blank"
                rel="noopener noreferrer"
                className="article-modal__link"
              >
                <ExternalLink size={16} className="article-modal__link-icon" />
                Read original
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleModal;
